/**
 * Main state management hook for Plan Guide.
 *
 * @see frontend/workspace/CU-868gzzjua/frontend_research.md - Q3 State Persistence
 * @see frontend/workspace/CU-868gzzjua/frontend_implementation_guide.md
 */

import { useState, useCallback, useEffect } from 'react';
import {
  useLazyGetPageQuery,
  useHandleActionMutation,
} from '../api/planGuideApi';
import type { PlanGuideState, GuideSession, C1Action } from '../types';
import {
  PAGE_IDS,
  INITIAL_SESSION,
  STORAGE_KEY_PREFIX,
  SESSION_EXPIRY_MS,
} from '../constants';

const INITIAL_STATE: PlanGuideState = {
  pageId: PAGE_IDS.HUB,
  title: '',
  c1Response: '', // Empty JSON string initially
  isLoading: true,
  error: null,
  session: INITIAL_SESSION,
  allowedTransitions: [],
  backPage: null,
  isTerminal: false,
  comparisonEnabled: false,
};

export const usePlanGuide = (
  planResultRootId: string,
  planResultTypeId: string
) => {
  const storageKey = `${STORAGE_KEY_PREFIX}_${planResultRootId}`;

  // Initialize state from localStorage if available
  const [state, setState] = useState<PlanGuideState>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Date.now() - parsed.timestamp < SESSION_EXPIRY_MS) {
          return {
            ...INITIAL_STATE,
            pageId: parsed.currentPageId || PAGE_IDS.HUB,
            session: parsed.session || INITIAL_SESSION,
          };
        }
      }
    } catch (e) {
      console.warn('[PlanGuide] Failed to restore state:', e);
    }
    return INITIAL_STATE;
  });

  const [getPage] = useLazyGetPageQuery();
  const [handleActionMutation] = useHandleActionMutation();

  // Persist state to localStorage
  useEffect(() => {
    const toStore = {
      currentPageId: state.pageId,
      session: state.session,
      planResultRootId,
      timestamp: Date.now(),
    };
    localStorage.setItem(storageKey, JSON.stringify(toStore));
  }, [state.pageId, state.session, planResultRootId, storageKey]);

  // Fetch a page
  const fetchPage = useCallback(
    async (pageId: string) => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const result = await getPage({
          planResultRootId,
          planResultTypeId,
          pageId,
          session: state.session,
        }).unwrap();

        // C1Component expects a JSON STRING, not a parsed object!
        // The backend sends json.dumps(c1_data), we keep it as a string (like Thesys Demo)
        const c1ResponseString = result.c1_response;

        setState({
          pageId: result.page_id,
          title: result.title,
          c1Response: c1ResponseString,
          isLoading: false,
          error: null,
          session: result.session || state.session || INITIAL_SESSION,
          allowedTransitions: result.allowed_transitions,
          backPage: result.back_page,
          isTerminal: result.is_terminal,
          comparisonEnabled: result.comparison_enabled ?? false,
          originalPlanResultRootId: result.original_plan_result_root_id,
          comparisonData: result.comparison_data,
        });
      } catch (err) {
        const errorMessage =
          (err as { data?: { error?: string }; status?: number })?.data
            ?.error || 'Failed to load page';
        const errorCode = (err as { status?: number })?.status;

        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: { message: errorMessage, code: errorCode },
        }));
      }
    },
    [getPage, planResultRootId, planResultTypeId, state.session]
  );

  // Handle action from C1 component
  const handleAction = useCallback(
    async (action: C1Action) => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const actionType = action.params?.action || action.type || '';
      const formData = action.params?.form_data as Record<string, unknown> | undefined;

      try {
        const result = await handleActionMutation({
          planResultRootId,
          planResultTypeId,
          action: {
            action: actionType as string,
            form_data: formData,
            current_page_id: state.pageId,
          },
          session: state.session,
        }).unwrap();

        // C1Component expects a JSON STRING, not a parsed object!
        // The backend sends json.dumps(c1_data), we keep it as a string (like Thesys Demo)
        const c1ResponseString = result.c1_response;

        setState({
          pageId: result.page_id,
          title: result.title,
          c1Response: c1ResponseString,
          isLoading: false,
          error: null,
          session: result.session || state.session || INITIAL_SESSION,
          allowedTransitions: result.allowed_transitions,
          backPage: result.back_page,
          isTerminal: result.is_terminal,
          comparisonEnabled: result.comparison_enabled ?? false,
          originalPlanResultRootId: result.original_plan_result_root_id,
          comparisonData: result.comparison_data,
        });
      } catch (err) {
        const errorMessage =
          (err as { data?: { error?: string }; status?: number })?.data
            ?.error || 'Action failed';
        const errorCode = (err as { status?: number })?.status;

        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: { message: errorMessage, code: errorCode },
        }));
      }
    },
    [
      handleActionMutation,
      planResultRootId,
      planResultTypeId,
      state.pageId,
      state.session,
    ]
  );

  // Go back to previous page
  const goBack = useCallback(() => {
    if (state.backPage) {
      fetchPage(state.backPage);
    }
  }, [state.backPage, fetchPage]);

  // Navigate to specific page
  const navigateTo = useCallback(
    (pageId: string) => {
      if (
        state.allowedTransitions.includes(pageId) ||
        pageId === state.backPage
      ) {
        fetchPage(pageId);
      }
    },
    [state.allowedTransitions, state.backPage, fetchPage]
  );

  // Go to hub
  const goToHub = useCallback(() => {
    fetchPage(PAGE_IDS.HUB);
  }, [fetchPage]);

  // Clear state (on close/complete)
  const clearState = useCallback(() => {
    localStorage.removeItem(storageKey);
    setState(INITIAL_STATE);
  }, [storageKey]);

  return {
    ...state,
    fetchPage,
    handleAction,
    goBack,
    navigateTo,
    goToHub,
    clearState,
  };
};
